import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, TextField, MenuItem, Chip, Tooltip, IconButton, Divider,
} from '@mui/material';
import {
  FolderOpen, CheckCircle, ErrorOutline, InsertDriveFile, Close,
} from '@mui/icons-material';
import api from '../../services/api';

// Fuzzy-match a name against beneficiaries → best id (mirrors BOEList logic)
function findBestBeneficiaryId(name, beneficiaries) {
  if (!name || !beneficiaries.length) return '';
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
  const norm = normalize(name);
  const words = norm.split(/\s+/).filter(w => w.length > 2);
  if (!words.length) return '';
  let bestId = '', bestScore = 0;
  for (const b of beneficiaries) {
    const bNorm = normalize(b.name);
    const bWords = bNorm.split(/\s+/).filter(w => w.length > 2);
    const overlap = words.filter(w => bWords.some(bw => bw.includes(w) || w.includes(bw))).length;
    const overlapScore = overlap / Math.max(words.length, bWords.length, 1);
    const containsScore = (bNorm.includes(norm) || norm.includes(bNorm)) ? 0.8 : 0;
    const score = Math.max(overlapScore, containsScore);
    if (score > bestScore) { bestScore = score; bestId = String(b.id); }
  }
  return bestScore >= 0.2 ? bestId : '';
}

// Recursively pull all files out of a dropped folder (webkitGetAsEntry tree)
async function readEntry(entry, files) {
  if (entry.isFile) {
    await new Promise((resolve) => entry.file(f => { files.push(f); resolve(); }, () => resolve()));
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    // readEntries returns batches (max ~100) — loop until empty
    const readBatch = () => new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));
    let batch;
    do {
      batch = await readBatch();
      for (const e of batch) await readEntry(e, files);
    } while (batch.length > 0);
  }
}

const num = (v) => v == null || v === '' ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });

export default function BulkBoeImport({ open, onClose, beneficiaries, onDone }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [rows, setRows]         = useState([]);        // editable parsed groups
  const [unmatched, setUnmatched] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults]   = useState(null);      // per-row outcome after import
  const folderInputRef = useRef();

  const reset = () => { setRows([]); setUnmatched([]); setError(''); setResults(null); setLoading(false); };

  const sendFiles = useCallback(async (files) => {
    const pdfs = files.filter(f => /\.(pdf|zip)$/i.test(f.name) && !f.name.startsWith('.'));
    if (!pdfs.length) { setError('No PDF or ZIP files found in the drop'); return; }
    setLoading(true); setError(''); setResults(null);
    try {
      const fd = new FormData();
      pdfs.forEach(f => fd.append('files', f, f.name));
      const res = await api.post('/boe/parse-bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { groups, unmatched: un } = res.data.data;
      setRows(groups.map(g => ({
        ...g,
        _checked: !!g.boe_number,
        beneficiary_id: findBestBeneficiaryId(g.consignee, beneficiaries),
      })));
      setUnmatched(un || []);
      if (!groups.length) setError('No Bill of Entry PDF found in the upload');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to parse files');
    }
    setLoading(false);
  }, [beneficiaries]);

  const onDrop = useCallback(async (e) => {
    e.preventDefault(); setDragging(false);
    const items = e.dataTransfer.items;
    const files = [];
    if (items && items.length && items[0].webkitGetAsEntry) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
      for (const entry of entries) await readEntry(entry, files);
    } else {
      for (const f of e.dataTransfer.files) files.push(f);
    }
    sendFiles(files);
  }, [sendFiles]);

  const updateRow = (idx, patch) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const selectedCount   = rows.filter(r => r._checked).length;
  const selectableCount = rows.filter(r => r.boe_number).length;

  const doImport = async () => {
    const entries = rows.filter(r => r._checked).map(r => ({
      boe_number: r.boe_number, hawb_number: r.hawb || null, boe_date: r.boe_date,
      port_of_entry: r.port_of_entry || 'INDEL4', beneficiary_id: r.beneficiary_id || null,
      currency: r.currency || 'USD',
      invoice_value: r.invoice_value, assessable_value: r.assessable_value,
      bcd_amount: r.bcd_amount, aidc_amount: r.aidc_amount,
      sw_surcharge: r.sw_surcharge, igst_amount: r.igst_amount,
      // Duty total = BCD + AIDC + SW (excludes IGST) — mirrors BOEList calcDuty
      duty_amount: ((parseFloat(r.bcd_amount) || 0) + (parseFloat(r.aidc_amount) || 0) + (parseFloat(r.sw_surcharge) || 0)).toFixed(2),
      total_value: r.total_value, items: r.items || [],
      boe_file_path: r.boe_file_path, invoice_file_path: r.invoice_file_path,
      awb_file_path: r.awb_file_path, fedex_invoice_path: r.fedex_invoice_path,
    }));
    if (!entries.length) { setError('Select at least one BOE to import'); return; }
    setImporting(true); setError('');
    try {
      const res = await api.post('/boe/bulk-create', { entries });
      setResults(res.data.results || []);
      if (res.data.created > 0 && onDone) onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Bulk import failed');
    }
    setImporting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }}
      maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Bulk Import BOEs (Folder / Multiple Files)
        <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Dropzone */}
        {!rows.length && !results && (
          <>
            <input ref={folderInputRef} type="file" hidden multiple webkitdirectory=""
              onChange={e => { sendFiles(Array.from(e.target.files)); e.target.value = ''; }} />
            <Box
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => folderInputRef.current?.click()}
              sx={{
                mt: 1, p: 5, border: '2px dashed',
                borderColor: dragging ? 'primary.main' : 'divider',
                borderRadius: 2, textAlign: 'center', cursor: 'pointer',
                bgcolor: dragging ? 'action.hover' : 'background.default',
                transition: 'all .2s', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={20} /><Typography>Parsing all files…</Typography>
                </Box>
              ) : (
                <>
                  <FolderOpen sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" fontWeight={600}>Drop a folder of BOE documents here</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Multiple Bills of Entry are detected, grouped by HAWB, and parsed together.
                    Click to pick a folder, or drop files / a ZIP.
                  </Typography>
                </>
              )}
            </Box>
          </>
        )}

        {/* Review table */}
        {rows.length > 0 && !results && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {rows.length} Bill(s) of Entry detected — review &amp; select to import
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small"
                      checked={selectableCount > 0 && selectedCount === selectableCount}
                      indeterminate={selectedCount > 0 && selectedCount < selectableCount}
                      onChange={e => setRows(rs => rs.map(r => ({ ...r, _checked: e.target.checked && !!r.boe_number })))} />
                  </TableCell>
                  <TableCell>BOE Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>HAWB</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">USD</TableCell>
                  <TableCell align="right">Assessable</TableCell>
                  <TableCell align="right">IGST</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell>Files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r._groupKey || i} hover sx={{ opacity: r.boe_number ? 1 : 0.55 }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={r._checked} disabled={!r.boe_number}
                        onChange={e => updateRow(i, { _checked: e.target.checked })} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" value={r.boe_number || ''}
                        onChange={e => updateRow(i, { boe_number: e.target.value, _checked: !!e.target.value })}
                        sx={{ minWidth: 90 }} InputProps={{ sx: { fontSize: 13 } }}
                        placeholder="not detected" />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.boe_date || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.hawb || '—'}</TableCell>
                    <TableCell>
                      <TextField select variant="standard" value={r.beneficiary_id || ''}
                        onChange={e => updateRow(i, { beneficiary_id: e.target.value })}
                        sx={{ minWidth: 150 }} InputProps={{ sx: { fontSize: 13 } }}>
                        <MenuItem value=""><em>— none —</em></MenuItem>
                        {beneficiaries.map(b => <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>{num(r.invoice_value)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>{num(r.assessable_value)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>{num(r.igst_amount)}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={r.items?.length || 0}
                        color={r.items?.length ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(r.files || []).map(f => (
                          <Tooltip key={f.saved_name} title={f.original_name}>
                            <Chip size="small" icon={<InsertDriveFile />} variant="outlined"
                              label={f.role === 'boe' ? 'BOE' : f.role === 'invoice' ? 'INV' :
                                     f.role === 'awb' ? 'AWB' : f.role === 'fedex_invoice' ? 'FDX' : '?'} />
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {unmatched.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  {unmatched.length} file(s) not matched to any BOE (e.g. combined labels, loose invoices) —
                  these are uploaded but not attached:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {unmatched.map(f => (
                    <Chip key={f.saved_name} size="small" variant="outlined" icon={<InsertDriveFile />}
                      label={f.original_name.length > 28 ? f.original_name.slice(0, 28) + '…' : f.original_name} />
                  ))}
                </Box>
              </>
            )}
          </>
        )}

        {/* Import results */}
        {results && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Import complete</Typography>
            <Table size="small">
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: 40 }}>
                      {r.ok ? <CheckCircle color="success" fontSize="small" />
                            : <ErrorOutline color="error" fontSize="small" />}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.boe_number}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: r.ok ? 'success.main' : 'error.main' }}>
                      {r.ok ? `Created (#${r.id})` : r.error}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {results ? (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {rows.length > 0 && (
              <Button variant="contained" onClick={doImport} disabled={importing || selectedCount === 0}
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}>
                {importing ? 'Importing…' : `Import ${selectedCount} BOE${selectedCount !== 1 ? 's' : ''}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
